import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useServicesData } from "@/hooks/useServicesData";
import { useAdditionalInfo } from "@/hooks/useAdditionalInfo";
import BookingButton from "@/components/BookingButton";
import usePageSEO from "@/hooks/usePageSEO";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatDuration, formatPrice } from "@/utils/serviceFormatters";
import { Link } from "react-router-dom";
import { ServicesSkeleton } from "@/components/services/ServicesSkeleton";
import { AdditionalInfoSkeleton } from "@/components/services/AdditionalInfoSkeleton";
import { cn } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Helmet } from "react-helmet";

const Services = () => {
  usePageSEO({
    title: "Nos soins | Bio Beauté Zen",
    description: "Découvrez ma carte de soins bio et naturels : soins du visage, massages, épilations. Prestations personnalisées dans mon institut à Montpellier.",
    canonical: "https://biobeautezen.com/soins-visage-massages-epilations-montpellier",
    ogTitle: "Carte des soins bio | Bio Beauté Zen Montpellier",
    ogDescription: "Soins du visage, massages bien-être, épilations naturelles. Découvrez ma carte de soins bio et naturels à Montpellier."
  });
  
  const { categories, isLoading: isLoadingServices, error: servicesError } = useServicesData();
  const { data: additionalInfo, isLoading: isLoadingInfo } = useAdditionalInfo();
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const isMobile = useIsMobile();
  
  const toggleService = (serviceId: string) => {
    setExpandedServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };
  
  const truncateDescription = (text: string, maxLength: number = 150): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };
  
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Accueil",
                item: "https://biobeautezen.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Carte des soins",
                item: "https://biobeautezen.com/soins-visage-massages-epilations-montpellier",
              },
            ],
          })}
        </script>
      </Helmet>
      <div className="pt-20 pb-4">
        <div className="container mx-auto px-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Carte des soins</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      <main aria-labelledby="services-heading" className="pb-16">
        {/* Hero Section */}
        <section className="bg-bbz-green/30 py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 id="services-heading" className="font-poppins text-4xl md:text-5xl font-semibold text-bbz-text mb-6">
              Carte des soins
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto">
              Découvrez ma sélection de soins bio et naturels pour prendre soin de vous dans le respect de votre peau et
              de l'environnement.
            </p>
          </div>
        </section>

        {/* Services Cards */}
        <section className="section-container" aria-labelledby="categories-heading">
          <h2 id="categories-heading" className="sr-only">
            Liste des catégories de soins
          </h2>
          {servicesError ? (
            <div className="text-center py-10">
              <p className="text-lg text-red-600 mb-4">⚠️ Impossible de charger les services pour le moment.</p>
              <p className="text-sm text-bbz-text/70">Veuillez rafraîchir la page ou réessayer plus tard.</p>
            </div>
          ) : isLoadingServices ? (
            <ServicesSkeleton />
          ) : (
            <div className={cn("columns-1 md:columns-2 gap-8 space-y-8", !isLoadingServices && "animate-fade-in")}>
              {Object.keys(categories).length > 0 ? (
                Object.values(categories)
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map((category) => {
                    return (
                      <article
                        key={category.id}
                        className="break-inside-avoid mb-8 overflow-hidden border-bbz-green/20 hover:shadow-lg transition-all"
                        aria-labelledby={`category-${category.id}`}
                      >
                        <Card>
                          <CardHeader className="bg-bbz-beige/30">
                            <CardTitle id={`category-${category.id}`} className="font-poppins">
                              {category.title}
                            </CardTitle>
                            <CardDescription>{category.description}</CardDescription>
                          </CardHeader>
                          <CardContent className="p-6">
                            <div className="space-y-4">
                              {category.services
                                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                                .map((service) => {
                                  const isExpanded = expandedServices.has(service.id);
                                  return (
                                    <div
                                      key={service.id}
                                      className="border-b border-bbz-green/30 pb-2 last:border-0 hover:bg-bbz-cream/50 p-3 rounded-md transition-colors"
                                      role="region"
                                      aria-labelledby={`service-${service.id}`}
                                    >
                                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                                        <div className="mb-2 md:mb-0">
                                          <h3 id={`service-${service.id}`} className="font-poppins text-lg font-medium">
                                            {service.name}
                                          </h3>
                                          {isMobile ? (
                                            <p className="text-sm text-bbz-text/80 mt-1">
                                              {formatDuration(service.duration)} • {formatPrice(service.price)}
                                            </p>
                                          ) : (
                                            <p className="text-xs text-bbz-text/80 mt-1">
                                              {formatDuration(service.duration)}
                                            </p>
                                          )}
                                        </div>
                                        {!isMobile && (
                                          <div className="font-medium text-lg">{formatPrice(service.price)}</div>
                                        )}
                                      </div>

                                      {service.description && (
                                        <Collapsible open={isExpanded} onOpenChange={() => toggleService(service.id)}>
                                          <div className="mt-2">
                                            <CollapsibleContent>
                                              <p className="text-sm text-bbz-text/90 mb-2">{service.description}</p>
                                            </CollapsibleContent>
                                            <CollapsibleTrigger className="flex items-center gap-1 text-sm text-bbz-green-dark hover:text-bbz-green-dark/80 transition-colors font-medium">
                                              {isExpanded ? "Voir moins" : "En savoir plus..."}
                                              <ChevronDown
                                                className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                              />
                                            </CollapsibleTrigger>
                                          </div>
                                        </Collapsible>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </CardContent>
                        </Card>
                      </article>
                    );
                  })
              ) : (
                <div className="col-span-2 text-center py-10">
                  <p className="text-lg text-bbz-text/70">Aucune catégorie de soins n'est disponible pour le moment.</p>
                </div>
              )}
            </div>
          )}

          <div className="text-center mt-12">
            <p className="text-lg mb-3">
              Prête à vous offrir un moment de bien-être ?{" "}
              <Link to="/contact-institut-montpellier" className="text-bbz-green-dark hover:underline font-medium">
                En cas de question, contactez-moi ici.
              </Link>
            </p>
            <BookingButton className="btn-primary inline-block" />
          </div>
        </section>

        {/* Informations complémentaires */}
        <section className="bg-bbz-beige/30 py-16" aria-labelledby="infos-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 id="infos-heading" className="section-title text-center mx-auto after:mx-auto">
                Informations complémentaires
              </h2>

              {isLoadingInfo ? (
                <AdditionalInfoSkeleton />
              ) : (
                <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-8 mt-10", !isLoadingInfo && "animate-fade-in")}>
                  <article className="card" aria-labelledby="approach-heading">
                    <h3 id="approach-heading" className="font-poppins text-xl font-medium mb-4">
                      Mon approche
                    </h3>
                    <p className="mb-4">{additionalInfo?.approach || "Information non disponible"}</p>
                  </article>

                  <article className="card" aria-labelledby="tips-heading">
                    <h3 id="tips-heading" className="font-poppins text-xl font-medium mb-4">
                      Bon à savoir
                    </h3>
                    <ul className="leaf-bullet">
                      {additionalInfo?.tips ? (
                        additionalInfo.tips.split("\n").map((tip, i) => <li key={i}>{tip}</li>)
                      ) : (
                        <li>Information non disponible</li>
                      )}
                    </ul>
                  </article>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Services;
